import { useState } from "react";
import { AdminLayout } from "../components/AdminLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Building2,
  Search,
  Plus,
  MapPin,
  Mail,
  Phone,
  Globe,
  Package
} from "lucide-react";

interface AdminOrgProps {
  onNavigate: (page: string) => void;
}

/**
 * Admin Org Page
 * Manage clinics and pharmacies
 */
export function AdminOrg({ onNavigate }: AdminOrgProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("clinics");

  // Mock data
  const clinics = [
    {
      id: "1",
      name: "Sapphire Medical Clinic",
      city: "London",
      postcode: "W1T 4TJ",
      email: "contact@sapphiremedical.co.uk",
      phone: "020 1234 5678",
      website: "https://sapphiremedical.co.uk",
      licenceNumber: "SMC-2021-001",
      isActive: true,
      patientCount: 1234,
    },
    {
      id: "2",
      name: "Curaleaf Clinic",
      city: "Manchester",
      postcode: "M1 1AD",
      email: "info@curaleafclinic.co.uk",
      phone: "0161 123 4567",
      website: "https://curaleafclinic.co.uk",
      licenceNumber: "CLC-2021-002",
      isActive: true,
      patientCount: 856,
    },
    {
      id: "3",
      name: "Cannabis Clinic Cardiff",
      city: "Cardiff",
      postcode: "CF10 1DD",
      email: "hello@cannabisclinic.wales",
      phone: "029 1234 5678",
      website: "https://cannabisclinic.wales",
      licenceNumber: "CCC-2022-003",
      isActive: true,
      patientCount: 432,
    },
  ];

  const pharmacies = [
    {
      id: "1",
      name: "IPS Pharma",
      city: "Birmingham",
      gln: "5060780370005",
      contactEmail: "dispensary@ipspharma.co.uk",
      productCount: 156,
    },
    {
      id: "2",
      name: "Dispensary Green",
      city: "Leeds",
      gln: "5060780370012",
      contactEmail: "orders@dispensarygreen.co.uk",
      productCount: 203,
    },
  ];

  const stats = {
    clinics: {
      total: clinics.length,
      active: clinics.filter(c => c.isActive).length,
      totalPatients: clinics.reduce((sum, c) => sum + c.patientCount, 0),
    },
    pharmacies: {
      total: pharmacies.length,
      totalProducts: pharmacies.reduce((sum, p) => sum + p.productCount, 0),
    },
  };

  return (
    <AdminLayout currentPage="admin-org" onNavigate={onNavigate}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl gradient-green flex items-center justify-center">
              <Building2 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl">Organizations</h1>
              <p className="text-muted-foreground">Manage clinics and pharmacies</p>
            </div>
          </div>
          <Button className="gap-2">
            <Plus className="w-4 h-4" />
            Add Organization
          </Button>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="clinics">
              Clinics ({stats.clinics.total})
            </TabsTrigger>
            <TabsTrigger value="pharmacies">
              Pharmacies ({stats.pharmacies.total})
            </TabsTrigger>
          </TabsList>

          {/* Clinics Tab */}
          <TabsContent value="clinics" className="space-y-6">
            {/* Stats */}
            <div className="grid sm:grid-cols-3 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Total Clinics</p>
                      <p className="text-2xl font-bold mt-1">{stats.clinics.total}</p>
                    </div>
                    <Building2 className="w-8 h-8 text-primary" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Active</p>
                      <p className="text-2xl font-bold mt-1">{stats.clinics.active}</p>
                    </div>
                    <Badge variant="default" className="bg-primary text-lg px-3 py-1">✓</Badge>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Total Patients</p>
                      <p className="text-2xl font-bold mt-1">{stats.clinics.totalPatients}</p>
                    </div>
                    <Package className="w-8 h-8 text-muted-foreground" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Search */}
            <Card>
              <CardContent className="pt-6">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search clinics..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Clinics List */}
            <div className="space-y-4">
              {clinics.map((clinic) => (
                <Card key={clinic.id}>
                  <CardContent className="pt-6">
                    <div className="space-y-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-semibold text-lg">{clinic.name}</h3>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline">{clinic.licenceNumber}</Badge>
                            {clinic.isActive && (
                              <Badge variant="default" className="bg-primary">Active</Badge>
                            )}
                          </div>
                        </div>
                        <Button variant="outline" size="sm">Edit</Button>
                      </div>

                      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                        <div className="flex items-start gap-2">
                          <MapPin className="w-4 h-4 text-muted-foreground mt-0.5" />
                          <div>
                            <p className="font-medium">{clinic.city}</p>
                            <p className="text-muted-foreground">{clinic.postcode}</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-2">
                          <Mail className="w-4 h-4 text-muted-foreground mt-0.5" />
                          <div>
                            <p className="font-medium break-all">{clinic.email}</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-2">
                          <Phone className="w-4 h-4 text-muted-foreground mt-0.5" />
                          <div>
                            <p className="font-medium">{clinic.phone}</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-2">
                          <Globe className="w-4 h-4 text-muted-foreground mt-0.5" />
                          <div>
                            <a 
                              href={clinic.website} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="font-medium text-primary hover:underline"
                            >
                              Website
                            </a>
                          </div>
                        </div>
                      </div>

                      <div className="pt-2 border-t">
                        <p className="text-sm text-muted-foreground">
                          <span className="font-semibold text-foreground">{clinic.patientCount}</span> registered patients
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Pharmacies Tab */}
          <TabsContent value="pharmacies" className="space-y-6">
            {/* Stats */}
            <div className="grid sm:grid-cols-2 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Total Pharmacies</p>
                      <p className="text-2xl font-bold mt-1">{stats.pharmacies.total}</p>
                    </div>
                    <Package className="w-8 h-8 text-primary" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Products Listed</p>
                      <p className="text-2xl font-bold mt-1">{stats.pharmacies.totalProducts}</p>
                    </div>
                    <Package className="w-8 h-8 text-muted-foreground" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Pharmacies List */}
            <div className="space-y-4">
              {pharmacies.map((pharmacy) => (
                <Card key={pharmacy.id}>
                  <CardContent className="pt-6">
                    <div className="space-y-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-semibold text-lg">{pharmacy.name}</h3>
                          <p className="text-sm text-muted-foreground">{pharmacy.city}</p>
                        </div>
                        <Button variant="outline" size="sm">Edit</Button>
                      </div>

                      <div className="grid sm:grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-muted-foreground">GLN</p>
                          <code className="font-mono">{pharmacy.gln}</code>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Contact</p>
                          <p className="font-medium">{pharmacy.contactEmail}</p>
                        </div>
                      </div>

                      <div className="pt-2 border-t">
                        <p className="text-sm text-muted-foreground">
                          <span className="font-semibold text-foreground">{pharmacy.productCount}</span> products in catalog
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
}
